import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";
import { Alert, AlertTitle } from "@material-ui/lab";
import { Button, InputAdornment, Snackbar, TextField } from "@material-ui/core";
import * as InventoryApi from "../../../api/storeInventoryApi";
import { isOk } from "../../../result";
import useStyles from "./styles";
import { SpoilageReasonSelect } from "../../../components/selects/SpoilageReasonSelect";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { ProductSpoilageReason } from "../../../models/Product";
import { Authenticated } from "../../../models/User";
import { UserContext } from "../../../UserProvider";
import moment from "moment";
import { useI18n } from "../../../hooks/useI18n";

interface Spoilage {
  reason?: ProductSpoilageReason;
  quantity: number;
  date?: Date;
  comment?: string;
}

interface ProductSpoilageProps {
  storeId: string;
  inventoryItem: any;
  onSave: (report: any) => void;
}

export const ReportSpoilage: FunctionComponent<ProductSpoilageProps> = ({
  storeId,
  inventoryItem,
  onSave
}: any) => {
  const classes = useStyles();
  const user = useContext(UserContext) as Authenticated;
  const { t } = useI18n();

  const [state, setState] = useState({
    spoilage: { date: new Date() } as Spoilage,
    spoilageReport: {} as any,
    errors: {} as any,
    problem: null as any
  });

  const saveSpoilageReport = async () => {
    const report = state.spoilage;

    if (!report?.reason) {
      return;
    }

    let errors = state.errors;

    errors.quantity =
      !report.quantity ||
      report.quantity < 0 ||
      report.quantity > inventoryItem.onShelf;
    errors.comment =
      report.reason.commentRequired &&
      (!report.comment || !report.comment.length);
    errors.date = !report.date;

    if (errors.quantity || errors.date || errors.comment) {
      return setState({ ...state, errors: errors });
    }

    const spoilageReport = {
      reasonId: report.reason.id,
      reasonName: report.reason.name,
      quantity: report.quantity,
      date: report.date
        ? moment(report.date).format("YYYY-MM-DD HH:mm")
        : moment().format("YYYY-MM-DD HH:mm"),
      comment: report.reason.commentRequired ? report.comment ?? "" : ""
    };

    const result = await InventoryApi.saveSpoilageReport(
      storeId,
      inventoryItem.product.id,
      spoilageReport
    );

    if (isOk(result)) {
      const completeSpoilageReport = {
        ...spoilageReport,
        id: result.value,
        reportedBy: user.name
      };
      setState({
        ...state,
        spoilageReport: completeSpoilageReport
      });
      return onSave(completeSpoilageReport);
    }

    return setState({
      ...state,
      problem: result.details
    });
  };

  return storeId && inventoryItem ? (
    <>
      <SpoilageReasonSelect
        className={classes.field}
        label={"Reason"}
        onSelected={reason => {
          return setState({
            ...state,
            spoilage: { ...state.spoilage, reason: reason }
          });
        }}
      />

      <TextField
        className={classes.field}
        error={state.errors.quantity}
        type="number"
        label="Quantity"
        value={state.spoilage.quantity || ""}
        helperText={`On shelf: ${inventoryItem.onShelf}st`}
        InputProps={{
          endAdornment: <InputAdornment position="end">st</InputAdornment>,
          inputProps: {
            max: inventoryItem.onShelf,
            min: 0
          }
        }}
        onChange={e =>
          setState({
            ...state,
            spoilage: {
              ...state.spoilage,
              quantity: !isNaN(Number(e.target.value))
                ? Number(e.target.value)
                : 0
            }
          })
        }
      />
      <KeyboardDatePicker
        className={classes.field}
        error={state.errors.date}
        minDate={
          inventoryItem &&
          inventoryItem.reportedStocktaking &&
          inventoryItem.reportedStocktaking.length > 0
            ? inventoryItem.reportedStocktaking[0].created
            : undefined
        }
        minDateMessage="Spoilage date cannot be passed latest delivery date"
        maxDate={new Date()}
        margin="normal"
        id="date-picker-dialog"
        label="Date"
        format="YYYY-MM-DD"
        value={state.spoilage.date}
        onChange={(date: MaterialUiPickersDate) => {
          return setState({
            ...state,
            spoilage: {
              ...state.spoilage,
              date: date && date.isValid() ? date.toDate() : undefined
            }
          });
        }}
        KeyboardButtonProps={{
          "aria-label": t("common.actions.changeDate")
        }}
      />

      {state.spoilage.reason?.commentRequired ? (
        <TextField
          className={classes.field}
          error={state.errors.comment}
          type="text"
          label="Comment"
          value={state.spoilage.comment}
          onChange={e =>
            setState({
              ...state,
              spoilage: {
                ...state.spoilage,
                comment: e.target.value
              }
            })
          }
        />
      ) : null}

      <div className={classes.actionArea}>
        <Button
          className={classes.actionButton}
          variant="contained"
          color="primary"
          onClick={saveSpoilageReport}
        >
          Report
        </Button>
      </div>

      <Snackbar
        open={state.problem != null}
        autoHideDuration={6000}
        onClose={() => setState({ ...state, problem: null })}
      >
        <Alert severity="error">
          <AlertTitle>
            {state.problem?.status}, {state.problem?.title}
          </AlertTitle>
          {state.problem?.detail} <strong>{state.problem?.traceId}</strong>
        </Alert>
      </Snackbar>
    </>
  ) : (
    <></>
  );
};
